import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RRNavLink } from 'react-router-dom';
import logo from '../../assets/general/intellicubix-logo.webp';

const MainContainer = styled.nav`
  width: 100%;
  background-color: ${(props) => props.theme.colors.black};

`;

const MainContent = styled.div`
  width: 100%;
  max-width: 1170px;
  margin: 0 auto;
  background-color: ${(props) => props.theme.colors.bgGray};
  padding: 0 20px;

  /* Mobile – stack rows */
  display: flex;
  flex-direction: column;

  /* Tablet & up – single row */
  @media (min-width: ${(props) => props.theme.screens.tablet}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 100%;
  }
`;

const TopRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Hamburger = styled.button`
  position: relative;
  width: 32px;
  height: 22px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  margin-right: 15px;
  z-index: 1000;

  /* Hide on tablet & up */
  @media (min-width: ${(props) => props.theme.screens.tablet}) {
    display: none;
  }

  span {
    display: block;
    width: 100%;
    height: 3px;
    background-color: ${(props) => props.theme.colors.white};
    transition: transform 0.3s ease, opacity 0.3s ease;
  }

  /* Animate to X when open */
  ${(props) =>
    props.isopen &&
    `
    span:nth-child(1) {
      transform: translateY(9.5px) rotate(45deg);
    }
    span:nth-child(2) {
      opacity: 0;
    }
    span:nth-child(3) {
      transform: translateY(-9.5px) rotate(-45deg);
    }
  `}
`;

const MobileNav = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: ${(props) => (props.isopen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 10px;
  background-color: rgba(0, 0, 0, 0.95);
  padding: 50px;
  z-index: 100;

  /* Only below tablet */
  @media (min-width: ${(props) => props.theme.screens.tablet}) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NavLinksContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 20px;
  margin-left: 20px;

  /* Hide on mobile */
  @media (max-width: ${(props) => props.theme.screens.tablet}) {
    display: none;
  }
`;

const Logo = styled.img`
  width: 100px;
  height: auto;
`;

// Link wrapper for the logo to route to homepage and remove default link styles
const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;

  span {
    margin-left: 10px;
    font-size: 18px;
    font-weight: 500;
    color: ${(props) => props.theme.colors.white};
    font-family: ${(props) => props.theme.fonts.headline};
    text-transform: capitalize;
  }
`;

const StyledNavLink = styled(RRNavLink)`
  font-size: 18px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.headline};
  text-transform: capitalize;
  letter-spacing: 1px;
  margin: 0 15px;
  text-decoration: none;
  transition: color 0.2s ease-in-out;

  &.active,
  &:hover {
    color: ${({ theme }) =>
    theme?.colors?.magenta ? theme.colors.magenta : '#84064F'};
  }

  @media (max-width: ${(props) => props.theme.screens.tablet}) {
    font-size: 23px;
  }
`;

const MainNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen((prev) => !prev);

  return (
    <MainContainer>
      <MainContent>
        <TopRow>
          <LogoContainer>
            <LogoLink to="/">
              <Logo src={logo} alt="Intellicubix logo" />
              <span>INTELLICUBIX</span>
            </LogoLink>
          </LogoContainer>

          {/* Desktop Links */}
          <NavLinksContainer>
            <StyledNavLink to="/" end>Home</StyledNavLink>
            <StyledNavLink to="/services">Services</StyledNavLink>
            <StyledNavLink to="/blog">Blog</StyledNavLink>
            <StyledNavLink to="/about">About</StyledNavLink>
            <StyledNavLink to="/contact">Contact</StyledNavLink>
          </NavLinksContainer>

          {/* Hamburger Icon */}
          <Hamburger onClick={toggleMenu} isopen={isOpen ? 1 : 0} aria-label="Toggle navigation menu">
            <span />
            <span />
            <span />
          </Hamburger>
        </TopRow>

        {/* Mobile Dropdown */}
        <MobileNav isopen={isOpen ? 1 : 0}>
          <StyledNavLink to="/" end onClick={() => setIsOpen(false)}>
            Home
          </StyledNavLink>
          <StyledNavLink to="/services" onClick={() => setIsOpen(false)}>
            Services
          </StyledNavLink>
          <StyledNavLink to="/blog" onClick={() => setIsOpen(false)}>
            Blog
          </StyledNavLink>
          <StyledNavLink to="/about" onClick={() => setIsOpen(false)}>
            About
          </StyledNavLink>
          <StyledNavLink to="/contact" onClick={() => setIsOpen(false)}>
            Contact
          </StyledNavLink>
        </MobileNav>
      </MainContent>
    </MainContainer>
  );
};

export default MainNav;